import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { LeadFilterState, FilterCounts } from '../types/leads';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Result from screening a single lead
 */
export interface ScreeningResult {
  lead_id: string;
  success: boolean;
  cap_rate?: number | null;
  noi?: number | null;
  coc_return?: number | null;
  lead_score?: number | null;
  error?: string;
  error_reason?: string;
}

/**
 * Response from batch screening endpoint
 */
export interface BatchScreeningResponse {
  total: number;
  successful: number;
  failed: number;
  results: ScreeningResult[];
}

/**
 * Lead from API response
 */
export interface LeadResult {
  id: string;
  address: string;
  formatted_address: string | null;
  neighborhood: string | null;
  neighborhood_tier: string;
  zip_code: string | null;
  total_units: number;
  market_value: number | null;
  cap_rate: number | null;
  lead_score: number | null;
  has_rental_license: boolean | null;
  rental_license_status: string | null;
  noi: number | null;
  year_built: number | null;
  coc_return?: number | null;  // Cash-on-cash if calculated
  screen_status?: 'screened' | 'pending' | 'failed' | null;
  thumbnail_url?: string | null;
}

/**
 * API response for leads endpoint
 */
export interface LeadListResponse {
  total: number;
  page: number;
  limit: number;
  pages: number;
  leads: LeadResult[];
  filter_counts?: FilterCounts;
}

/**
 * Build query string from filter state
 */
export function buildQueryString(filters: LeadFilterState, includeCounts = false): string {
  const params = new URLSearchParams();

  if (filters.tiers.length > 0) params.set('tiers', filters.tiers.join(','));
  if (filters.zipCodes.length > 0) params.set('zip_codes', filters.zipCodes.join(','));
  if (filters.unitMin !== null) params.set('unit_min', String(filters.unitMin));
  if (filters.unitMax !== null) params.set('unit_max', String(filters.unitMax));
  if (filters.scoreMin !== null) params.set('score_min', String(filters.scoreMin));
  if (filters.scoreMax !== null) params.set('score_max', String(filters.scoreMax));
  if (filters.licenseStatus !== 'any') params.set('license_status', filters.licenseStatus);
  if (filters.marketStatus !== 'any') params.set('market_status', filters.marketStatus);
  if (filters.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters.dateTo) params.set('date_to', filters.dateTo);
  params.set('sort_by', filters.sortBy);
  params.set('sort_order', filters.sortOrder);
  params.set('page', String(filters.page));
  params.set('limit', String(filters.limit));
  if (includeCounts) params.set('include_counts', 'true');

  return params.toString();
}

/**
 * Fetch leads from API
 */
export async function fetchLeads(
  filters: LeadFilterState,
  includeCounts = false
): Promise<LeadListResponse> {
  const queryString = buildQueryString(filters, includeCounts);
  const response = await fetch(`${API_BASE}/api/leads?${queryString}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * React Query hook for leads
 */
export function useLeads(
  filters: LeadFilterState,
  options?: Omit<UseQueryOptions<LeadListResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: () => fetchLeads(filters, true),
    staleTime: 30_000,  // 30 seconds
    ...options,
  });
}

/**
 * Screen a single lead
 *
 * Sends the lead to the screening pipeline to calculate cap rate, NOI, etc.
 * Returns the screening result with calculated values or error information.
 */
export async function screenLead(leadId: string): Promise<ScreeningResult> {
  try {
    const response = await fetch(`${API_BASE}/api/screen`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lead_id: leadId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      return {
        lead_id: leadId,
        success: false,
        error: `HTTP ${response.status}`,
        error_reason: errorData.detail || 'Screening failed',
      };
    }

    return response.json();
  } catch (error) {
    return {
      lead_id: leadId,
      success: false,
      error: 'Network error',
      error_reason: error instanceof Error ? error.message : 'Failed to connect to server',
    };
  }
}

/**
 * Screen multiple leads in a batch
 *
 * Sends multiple leads to the screening pipeline for batch processing.
 * Returns aggregated results with success/failure counts.
 */
export async function screenLeadsBatch(leadIds: string[]): Promise<BatchScreeningResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/batch/screen`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lead_ids: leadIds }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      // Return a failed batch response for all leads
      return {
        total: leadIds.length,
        successful: 0,
        failed: leadIds.length,
        results: leadIds.map((id) => ({
          lead_id: id,
          success: false,
          error: `HTTP ${response.status}`,
          error_reason: errorData.detail || 'Batch screening failed',
        })),
      };
    }

    return response.json();
  } catch (error) {
    // Return a failed batch response for all leads on network error
    return {
      total: leadIds.length,
      successful: 0,
      failed: leadIds.length,
      results: leadIds.map((id) => ({
        lead_id: id,
        success: false,
        error: 'Network error',
        error_reason: error instanceof Error ? error.message : 'Failed to connect to server',
      })),
    };
  }
}
